import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  AcUnit,
  Favorite,
  Celebration,
  Flag,
  Star,
  LocalFlorist,
  VolunteerActivism,
  Circle,
  CardGiftcard
} from '@mui/icons-material';

// 1. Define Animations
const fall = keyframes`
  0% { transform: translateY(-10vh) rotate(0deg) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  100% { transform: translateY(110vh) rotate(360deg) translateX(20px); opacity: 0; }
`;

const float = keyframes`
  0% { transform: translateY(110vh) scale(0.5) translateX(0); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% { transform: translateY(-10vh) scale(1.2) translateX(20px); opacity: 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.5); opacity: 1; }
  100% { transform: scale(1); opacity: 0.5; }
`;

// 2. Icon Mapping
const getIconAndConfig = (iconName, primaryColor, secondaryColor) => {
  switch (iconName) {
    case 'AcUnit': // Christmas/Winter
      return {
        icon: AcUnit,
        animation: fall,
        count: 50,
        colors: ['#fff', '#e3f2fd', '#bbdefb'], // Snow colors
        spin: true
      };
    case 'Favorite': // Valentines
    case 'VolunteerActivism':
      return {
        icon: Favorite,
        animation: float,
        count: 30,
        colors: ['#e91e63', '#f48fb1', '#fce4ec', '#d81b60'], // Pink/Red
        spin: false
      };
    case 'Celebration': // New Year/Fiesta
    case 'CardGiftcard':
      return {
        icon: Circle, // Confetti dots
        animation: fall,
        count: 70,
        colors: ['#f44336', '#2196f3', '#ffeb3b', '#4caf50', '#9c27b0'], // Multicolor
        spin: true
      };
    case 'Flag': // Independence
      return {
        icon: Star, // Stars for flag theme usually look better than falling flags
        animation: float,
        count: 40,
        colors: ['#1976d2', '#d32f2f', '#ffca28', '#ffffff'], // Blue, Red, Yellow, White
        spin: true
      };
    case 'LocalFlorist': // Spring/Easter
      return {
        icon: LocalFlorist,
        animation: fall,
        count: 25,
        colors: ['#81c784', '#fff176', '#f06292', '#ba68c8'], // Pastel flowers
        spin: true
      };
    case 'Star': // Default
    default:
      return {
        icon: Star,
        animation: float,
        count: 35,
        colors: [primaryColor, secondaryColor, '#fff', '#ffd700'],
        spin: true
      };
  }
};

const EventAnimations = ({ iconName, primaryColor, secondaryColor }) => {
  const [particles, setParticles] = useState([]);
  const config = getIconAndConfig(iconName, primaryColor, secondaryColor);
  const IconComponent = config.icon;

  useEffect(() => {
    // Generate static particles on mount to avoid re-renders
    const newParticles = Array.from({ length: config.count }).map((_, i) => ({
      id: i,
      left: Math.random() * 100, // 0-100% width
      delay: Math.random() * 5, // 0-5s delay
      duration: 3 + Math.random() * 4, // 3-7s duration
      size: 10 + Math.random() * 20, // 10-30px size
      color: config.colors[Math.floor(Math.random() * config.colors.length)],
      offset: (Math.random() - 0.5) * 50 // -25 to +25px horizontal drift
    }));
    setParticles(newParticles);
  }, [iconName, config.count]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 1400, // Above Dialog (usually 1300)
        overflow: 'hidden'
      }}
    >
      {particles.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            left: `${p.left}%`,
            top: -50, // Start just above screen
            color: p.color,
            animation: `${config.animation} ${p.duration}s linear infinite`,
            animationDelay: `${p.delay}s`,
            opacity: 0, // Start invisible until animation kicks in
          }}
        >
          <IconComponent style={{ fontSize: p.size }} />
        </Box>
      ))}
    </Box>
  );
};

export default EventAnimations;
